import { useEffect, useMemo, useState } from "react";
import type { Club, Post, PostAuthor } from "../data/models";
import { useServices } from "../data/services";
import { useAsync } from "../data/useAsync";
import { percent, eventWhen } from "../data/format";
import { haptic, openTelegram } from "../platform/telegram";
import { useNav, type ConnectSection } from "../nav/router";
import { Screen } from "../nav/Shell";
import { Avatar, Button, Card, CourseBadge, HandoffButton, Pill } from "../components/primitives";
import { PostCard } from "../components/PostCard";
import { EmptyState, ErrorState, FeedSkeleton, Skeleton } from "../components/states";
import { makePostActions } from "./postActions";
import "./connect.css";

/**
 * Connect — "the handoff is the feature."
 *
 * There is no in-app messaging; Telegram is the inbox. Every conversation
 * affordance here is the blue handoff button, and it always leaves the canvas.
 */

const SECTIONS: { id: ConnectSection; label: string }[] = [
  { id: "events", label: "Events" },
  { id: "clubs", label: "Clubs" },
  { id: "peers", label: "Peers" },
  { id: "matches", label: "Matches" },
  { id: "rivals", label: "Rivals" },
];

export function ConnectTab() {
  const { connectSection, setConnectSection } = useNav();

  return (
    <Screen>
      <div className="chips" role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === connectSection}
            className={`chip${s.id === connectSection ? " chip--active" : ""}`}
            onClick={() => {
              haptic.select();
              setConnectSection(s.id);
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {connectSection === "events" ? <EventsSection /> : null}
      {connectSection === "clubs" ? <ClubsSection /> : null}
      {connectSection === "peers" ? <PeersSection /> : null}
      {connectSection === "matches" ? <MatchesSection /> : null}
      {connectSection === "rivals" ? <RivalsSection /> : null}
    </Screen>
  );
}

/* ---------------- Events ---------------- */

function EventsSection() {
  const { feed } = useServices();
  const { push } = useNav();
  const load = useAsync(() => feed.getPostsByType("event", 0, 10), []);

  const [posts, setPosts] = useState<Post[]>([]);
  useEffect(() => {
    if (load.status === "ready") setPosts(load.data);
  }, [load.status, load.data]);

  const actions = useMemo(
    () => makePostActions(feed, setPosts, (post) => push({ name: "postDetail", postId: post.id })),
    [feed, push],
  );

  if (load.status === "loading") return <FeedSkeleton />;
  if (load.status === "error") return <ErrorState onRetry={load.reload} />;
  if (posts.length === 0) {
    return <EmptyState title="Quiet week" body="New campus events land here as clubs post them." />;
  }

  return (
    <div className="stack">
      {posts.map((p) => (
        <PostCard key={p.id} post={p} actions={actions} />
      ))}
    </div>
  );
}

/* ---------------- Clubs ---------------- */

function ClubsSection() {
  const { event } = useServices();
  const load = useAsync(() => event.getClubs(), []);

  const [clubs, setClubs] = useState<Club[]>([]);
  useEffect(() => {
    if (load.status === "ready") setClubs(load.data);
  }, [load.status, load.data]);

  const join = async (club: Club) => {
    setClubs((cs) =>
      cs.map((c) =>
        c.id === club.id ? { ...c, isMember: true, memberCount: c.memberCount + 1 } : c,
      ),
    );
    haptic.success();
    await event.joinClub(club.id);
  };

  if (load.status === "loading") return <FeedSkeleton count={2} />;
  if (load.status === "error") return <ErrorState onRetry={load.reload} />;

  return (
    <div className="stack">
      {clubs.map((club) => (
        <Card key={club.id}>
          <div className="card__body">
            <div className="club-head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="card__title">{club.name}</div>
                <div className="card__meta">
                  {club.memberCount} members
                  {club.nextMeeting ? ` · next ${eventWhen(club.nextMeeting)}` : ""}
                </div>
              </div>
              {club.isMember ? (
                <Pill tone="state">
                  <i className="fa-solid fa-check" aria-hidden="true" /> Member
                </Pill>
              ) : (
                <Button onClick={() => void join(club)}>Join</Button>
              )}
            </div>
            <div className="card__content">{club.description}</div>
            {club.isMember && club.groupChatId ? (
              <HandoffButton
                block
                label="Open club chat in Telegram"
                onOpen={() => openTelegram("https://t.me/")}
              />
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Peers ---------------- */

/**
 * Peers are proximity, not algorithm: same dorm or same course. The row is the
 * match row minus the % — deliberately, so the two read as siblings.
 */
function PeersSection() {
  const { feed } = useServices();
  const { push } = useNav();

  const load = useAsync(async () => {
    const users = await feed.searchUsers("", 12);
    const shared = await Promise.all(users.map((u) => feed.getSharedCourses(u.id)));
    return users
      .map((user, i) => ({ user, sharedCourse: shared[i][0] }))
      .filter((p) => p.user.id !== "user-001");
  }, []);

  if (load.status === "loading") return <FeedSkeleton count={2} />;
  if (load.status === "error") return <ErrorState onRetry={load.reload} />;

  return (
    <div className="stack">
      {load.data.map(({ user, sharedCourse }) => (
        <PeerRow
          key={user.id}
          user={user}
          sharedCourse={sharedCourse}
          onOpen={() => push({ name: "publicProfile", userId: user.id })}
        />
      ))}
    </div>
  );
}

function PeerRow({
  user,
  sharedCourse,
  onOpen,
}: {
  user: PostAuthor;
  sharedCourse?: string;
  onOpen: () => void;
}) {
  return (
    <Card compact onClick={onOpen}>
      <div className="peer-row">
        <Avatar id={user.id} name={user.displayName} photoURL={user.profilePhotoURL} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="course-row__name">{user.displayName}</div>
          <div className="course-row__meta">
            {user.school ?? "UT Austin"} '29
            {sharedCourse ? (
              <>
                {" "}
                · <CourseBadge code={sharedCourse} compact suffix="shared" />
              </>
            ) : null}
          </div>
        </div>
        <HandoffButton label="Say hi" onOpen={() => openTelegram("https://t.me/")} />
      </div>
    </Card>
  );
}

/* ---------------- Matches ---------------- */

function MatchesSection() {
  const { matching, feed } = useServices();
  const { push } = useNav();

  const load = useAsync(async () => {
    const matches = await matching.getMatches(0, 8);
    const shared = await Promise.all(matches.map((m) => feed.getSharedCourses(m.user.id)));
    return matches.map((match, i) => ({ match, sharedCourse: shared[i][0] }));
  }, []);

  return (
    <>
      {/* The roommate beat — brand tint, same D2 surface rule as the GPA hero. */}
      <div className="gpa-hero" style={{ margin: "var(--space-1) 0 var(--space-3)" }}>
        <div className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
          Roommate matching
        </div>
        <div className="t-body" style={{ color: "var(--text)" }}>
          Results Thursday. Your questionnaire is 90% done — one question left.
        </div>
      </div>

      {load.status === "loading" ? (
        <FeedSkeleton count={2} />
      ) : load.status === "error" ? (
        <ErrorState onRetry={load.reload} />
      ) : load.data.length === 0 ? (
        <EmptyState
          title="No matches yet — that's normal"
          body="Matching runs after your questionnaire. Results land Thursday."
        />
      ) : (
        <div className="stack">
          {load.data.map(({ match, sharedCourse }) => (
            <Card
              key={match.id}
              compact
              onClick={() => push({ name: "matchDetail", userId: match.user.id })}
            >
              <div className="peer-row">
                <Avatar
                  id={match.user.id}
                  name={match.user.displayName}
                  photoURL={match.user.profilePhotoURL}
                  size={40}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="course-row__name">{match.user.displayName}</div>
                  <div className="course-row__meta">
                    {/* % is always mint, never a gauge. */}
                    <span className="match-percent">{percent(match.overallScore)}</span>
                    {match.user.major ? ` · ${match.user.major}` : ""}
                    {sharedCourse ? ` · ${sharedCourse} shared` : ""}
                  </div>
                </div>
                <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------------- Rivals & activities ---------------- */

function RivalsSection() {
  const { feed } = useServices();
  const rivalsLoad = useAsync(() => feed.getPostsByType("rival", 0, 10), []);
  const activitiesLoad = useAsync(() => feed.getPostsByType("activity", 0, 10), []);

  const [rivals, setRivals] = useState<Post[]>([]);
  const [activities, setActivities] = useState<Post[]>([]);
  useEffect(() => {
    if (rivalsLoad.status === "ready") setRivals(rivalsLoad.data);
  }, [rivalsLoad.status, rivalsLoad.data]);
  useEffect(() => {
    if (activitiesLoad.status === "ready") setActivities(activitiesLoad.data);
  }, [activitiesLoad.status, activitiesLoad.data]);

  const rivalActions = useMemo(() => makePostActions(feed, setRivals), [feed]);
  const activityActions = useMemo(() => makePostActions(feed, setActivities), [feed]);

  if (rivalsLoad.status === "loading") return <FeedSkeleton />;
  if (rivalsLoad.status === "error") return <ErrorState onRetry={rivalsLoad.reload} />;

  return (
    <div className="stack">
      {rivals.map((p) => (
        <PostCard key={p.id} post={p} actions={rivalActions} />
      ))}
      {activities.length > 0 ? (
        <div className="section-header" style={{ padding: "var(--space-2) 0 0" }}>
          <span className="t-micro" style={{ color: "var(--text-hint)" }}>
            Open activities
          </span>
        </div>
      ) : null}
      {activities.map((p) => (
        <PostCard key={p.id} post={p} actions={activityActions} />
      ))}
    </div>
  );
}

/* ---------------- Match detail (pushed) ---------------- */

export function MatchDetail({ userId }: { userId: string }) {
  const { matching, feed } = useServices();
  const compat = useAsync(() => matching.getCompatibility(userId), [userId]);
  const vibe = useAsync(() => matching.getVibeProfile(userId), [userId]);
  const user = useAsync(() => feed.getUser(userId), [userId]);
  const shared = useAsync(() => feed.getSharedCourses(userId), [userId]);

  if (compat.status === "error") {
    return (
      <Screen>
        <ErrorState onRetry={compat.reload} />
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="match-head">
        <Avatar
          id={userId}
          name={user.data?.displayName ?? ""}
          photoURL={user.data?.profilePhotoURL}
          size={56}
        />
        <div>
          <h1 className="t-title">
            {user.data?.displayName ?? <Skeleton height={22} width={140} />}
          </h1>
          <div className="course-row__meta">
            {user.data ? `${user.data.school} '29 · ${user.data.major}` : ""}
          </div>
        </div>
      </div>

      {compat.status === "ready" ? (
        <>
          <div className="match-score">
            <span className="match-score__value">{percent(compat.data.overallScore)}</span>
            <span className="match-score__label">match</span>
          </div>

          <div className="card__row" style={{ marginBottom: "var(--space-3)" }}>
            {(shared.data ?? []).map((code) => (
              <CourseBadge key={code} code={code} compact suffix="shared" />
            ))}
            {(compat.data.sharedInterests ?? []).map((tag) => (
              <Pill key={tag} tone="state">
                {tag}
              </Pill>
            ))}
          </div>

          {compat.data.narrative ? (
            <Card compact>
              <div className="card__body">
                <span className="t-micro" style={{ color: "var(--text-hint)" }}>
                  Why you match
                </span>
                <div className="card__content">{compat.data.narrative}</div>
              </div>
            </Card>
          ) : null}

          <div className="stack" style={{ marginTop: "var(--space-3)" }}>
            {compat.data.subScores.map((s) => (
              <div key={s.dimension} className="subscore-row">
                <div className="subscore-row__head">
                  <span className="course-row__name">{s.dimension}</span>
                  <span className="match-percent">{percent(s.score)}</span>
                </div>
                <div className="course-row__meta">{s.label}</div>
              </div>
            ))}
          </div>

          {vibe.data?.vibeAnalysisText ? (
            <Card compact>
              <div className="card__body" style={{ marginTop: 0 }}>
                <span className="t-micro" style={{ color: "var(--text-hint)" }}>
                  Their vibe
                </span>
                <div className="card__content">{vibe.data.vibeAnalysisText}</div>
              </div>
            </Card>
          ) : null}

          <div className="match-actions">
            <Button kind="secondary" block onClick={() => openTelegram("https://t.me/")}>
              Add to a group chat
            </Button>
            {/* MainButton territory in the comp; in-canvas the handoff button
                carries the commitment, and it still always leaves the canvas. */}
            <HandoffButton
              block
              label="Say hi on Telegram"
              onOpen={() => openTelegram("https://t.me/")}
            />
          </div>
        </>
      ) : (
        <FeedSkeleton count={2} />
      )}
    </Screen>
  );
}
